import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiFileText,
  FiCheckCircle,
} from "react-icons/fi";

const UserDetailView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Get auth token
  const getAuthToken = () => {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("token")
    );
  };

  // Fetch user details
  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();
      if (!token) throw new Error("No authentication token found. Please log in.");

      const response = await fetch(
        `https://tas.itl.ng/api/v1/admins/members/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (response.status === 404) throw new Error("User not found");
      if (response.status === 401)
        throw new Error("Authentication failed. Please log in again.");
      if (!response.ok)
        throw new Error(`Failed to fetch user details: ${response.status}`);

      const result = await response.json();
      if (result.success) setUser(result.data);
      else throw new Error("API returned unsuccessful response");
    } catch (err) {
      setError(err.message);
      console.error("Error fetching user details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchUserDetails();
  }, [userId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  if (loading)
    return (
      <div
        style={{
          padding: "20px",
          background: "#fff",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "14px",
        }}
      >
        Loading user details...
      </div>
    );

  if (error)
    return (
      <div style={{ padding: "20px", background: "#fff", minHeight: "100vh" }}>
        <button
          onClick={() => navigate(-1)}
          style={smallBackButtonStyle}
        >
          <FiChevronLeft />
        </button>
        <div style={errorBoxStyle}>
          <h3>Error Loading User</h3>
          <p>{error}</p>
          <button onClick={fetchUserDetails} style={retryButtonStyle}>
            Retry
          </button>
        </div>
      </div>
    );

  if (!user)
    return (
      <div style={{ padding: "20px", background: "#fff", minHeight: "100vh" }}>
        <button
          onClick={() => navigate(-1)}
          style={smallBackButtonStyle}
        >
          <FiChevronLeft />
        </button>
        <div style={{ textAlign: "center", color: "#6b7280" }}>
          <h3>User Not Found</h3>
          <p>The requested user could not be found.</p>
        </div>
      </div>
    );

  return (
    <div style={{ background: "#fff", minHeight: "100vh", padding: "20px", maxWidth: "1000px", margin: "0 auto" }}>
      {/* Small Back Button */}
      <button
        onClick={() => navigate(-1)}
        style={smallBackButtonStyle}
      >
        <FiChevronLeft />
      </button>

      {/* Header */}
      <div style={headerContainer}>
        <div style={avatarContainer}>
          {user.personal_information?.full_name
            ?.split(" ")
            .map((n) => n[0])
            .join("")}
        </div>

        <div style={{ flex: 1 }}>
          <h2 style={nameStyle}>{user.personal_information?.full_name}</h2>
          <p style={roleStyle}>
            {user.membership_details?.membership_category || "Member"}
          </p>
          <div style={statusBadgeStyle(user.membership_details?.status)}>
            {user.membership_details?.status}
          </div>
          <p style={membershipIdStyle}>
            Membership ID:{" "}
            <span style={{ color: "#111827" }}>
              {user.membership_id || "N/A"}
            </span>
          </p>
        </div>
      </div>

      {/* Main layout */}
      <div style={mainLayout}>
        {/* Quick Info Left */}
        <div style={quickInfoBox}>
          <h4 style={quickInfoTitle}>Quick Info</h4>
          <div style={infoItem}>
            <FiMail />
            <span>{user.contact_information?.email || "N/A"}</span>
          </div>
          <div style={infoItem}>
            <FiPhone />
            <span>{user.contact_information?.whatsapp_number || "N/A"}</span>
          </div>
          <div style={infoItem}>
            <FiMapPin />
            <span>
              {user.geographic_information?.state?.name || "N/A"}, Nigeria
            </span>
          </div>
          <div style={infoItem}>
            <FiCalendar />
            <span>Joined {formatDate(user.timestamps?.created_at)}</span>
          </div>
          <div style={infoItem}>
            <FiUser />
            <span>Reports to Super Admin</span>
          </div>
        </div>

        {/* Right Tabs */}
        <div style={{ flex: 1, minWidth: "60%" }}>
          {/* Tabs */}
          <div style={tabHeader}>
            {["overview", "personal", "geographic", "documents"].map((tab) => (
              <div
                key={tab}
                style={{
                  ...tabButton,
                  borderBottom:
                    activeTab === tab ? "2px solid #2563eb" : "2px solid transparent",
                  color: activeTab === tab ? "#2563eb" : "#6b7280",
                }}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </div>
            ))}
          </div>

          {/* Tab Content */}
          <div style={tabContent}>
            {activeTab === "overview" && (
              <div>
                <h4 style={sectionTitle}>Membership Summary</h4>
                <div style={summaryGrid}>
                  <div style={summaryItem}>
                    <strong>Membership ID:</strong> {user.membership_id}
                  </div>
                  <div style={summaryItem}>
                    <strong>Category:</strong> {user.membership_details?.membership_category}
                  </div>
                  <div style={summaryItem}>
                    <strong>Status:</strong> {user.membership_details?.status}
                  </div>
                  <div style={summaryItem}>
                    <strong>Status Reason:</strong> {user.membership_details?.status_reason || "N/A"}
                  </div>
                  <div style={summaryItem}>
                    <strong>Roles:</strong> {user.membership_details?.roles?.join(", ") || "None"}
                  </div>
                  <div style={summaryItem}>
                    <strong>Member Since:</strong> {formatDate(user.timestamps?.created_at)}
                  </div>
                  <div style={summaryItem}>
                    <strong>Last Updated:</strong> {formatDate(user.timestamps?.updated_at)}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "personal" && (
              <div>
                <h4 style={sectionTitle}>Personal Information</h4>
                <div style={summaryGrid}>
                  <div style={summaryItem}>
                    <strong>Full Name:</strong> {user.personal_information?.full_name}
                  </div>
                  <div style={summaryItem}>
                    <strong>First Name:</strong> {user.personal_information?.first_name}
                  </div>
                  <div style={summaryItem}>
                    <strong>Other Name:</strong> {user.personal_information?.other_name || "N/A"}
                  </div>
                  <div style={summaryItem}>
                    <strong>Surname:</strong> {user.personal_information?.surname}
                  </div>
                  <div style={summaryItem}>
                    <strong>Gender:</strong> {user.personal_information?.gender}
                  </div>
                  <div style={summaryItem}>
                    <strong>Date of Birth:</strong> {formatDate(user.personal_information?.date_of_birth)}
                  </div>
                  <div style={summaryItem}>
                    <strong>NIN:</strong> {user.personal_information?.nin}
                  </div>
                  <div style={summaryItem}>
                    <strong>Address:</strong> {user.personal_information?.full_house_address}
                  </div>
                </div>

                <h4 style={{ ...sectionTitle, marginTop: "24px" }}>Contact Information</h4>
                <div style={summaryGrid}>
                  <div style={summaryItem}>
                    <strong>Email:</strong> {user.contact_information?.email}
                  </div>
                  <div style={summaryItem}>
                    <strong>Email Verified:</strong> {user.contact_information?.email_verified_at ? "Yes" : "No"}
                  </div>
                  <div style={summaryItem}>
                    <strong>WhatsApp Number:</strong> {user.contact_information?.whatsapp_number}
                  </div>
                  <div style={summaryItem}>
                    <strong>Telephone Numbers:</strong> {user.contact_information?.telephone_numbers?.join(", ") || "N/A"}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "geographic" && (
              <div>
                <h4 style={sectionTitle}>Geographic Information</h4>
                <div style={summaryGrid}>
                  <div style={summaryItem}>
                    <strong>State:</strong> {user.geographic_information?.state?.name} ({user.geographic_information?.state?.code})
                  </div>
                  <div style={summaryItem}>
                    <strong>Senatorial District:</strong> {user.geographic_information?.senatorial_district?.name} ({user.geographic_information?.senatorial_district?.code})
                  </div>
                  <div style={summaryItem}>
                    <strong>Federal Constituency:</strong> {user.geographic_information?.federal_constituency?.name} ({user.geographic_information?.federal_constituency?.code})
                  </div>
                  <div style={summaryItem}>
                    <strong>Local Government:</strong> {user.geographic_information?.local_government?.name} ({user.geographic_information?.local_government?.code})
                  </div>
                  <div style={summaryItem}>
                    <strong>Ward:</strong> {user.geographic_information?.ward?.name} ({user.geographic_information?.ward?.code})
                  </div>
                  <div style={summaryItem}>
                    <strong>Polling Unit:</strong> {user.geographic_information?.polling_unit?.name} ({user.geographic_information?.polling_unit?.code})
                  </div>
                </div>
              </div>
            )}

            {activeTab === "documents" && (
              <div>
                <h4 style={sectionTitle}>Document Status</h4>
                <div style={summaryGrid}>
                  <div style={summaryItem}>
                    <strong>Passport Photo:</strong> 
                    <span style={{ color: user.documents?.passport_photo ? "#16a34a" : "#dc2626", marginLeft: "8px" }}>
                      {user.documents?.passport_photo ? "Uploaded" : "Not Uploaded"}
                    </span>
                  </div>
                  <div style={summaryItem}>
                    <strong>Means of Identification:</strong>
                    <span style={{ color: user.documents?.means_of_identification ? "#16a34a" : "#dc2626", marginLeft: "8px" }}>
                      {user.documents?.means_of_identification ? "Uploaded" : "Not Uploaded"}
                    </span>
                  </div>
                  <div style={summaryItem}>
                    <strong>Party ID Card:</strong>
                    <span style={{ color: user.documents?.party_id_card ? "#16a34a" : "#dc2626", marginLeft: "8px" }}>
                      {user.documents?.party_id_card ? "Uploaded" : "Not Uploaded"}
                    </span>
                  </div>
                </div>
                
                {!user.documents?.passport_photo && !user.documents?.means_of_identification && !user.documents?.party_id_card && (
                  <div style={noDocumentsStyle}>
                    <FiFileText size={24} />
                    <p>No documents uploaded yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* --- Inline Styles --- */
const smallBackButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  padding: "6px",
  fontSize: "14px",
  color: "#374151",
  cursor: "pointer",
  marginBottom: "20px",
};

const headerContainer = {
  display: "flex",
  alignItems: "center",
  gap: "20px",
  marginBottom: "24px",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "16px",
};

const avatarContainer = {
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  background: "#2563eb",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: "bold",
  fontSize: "20px",
};

const nameStyle = { margin: "0", fontSize: "18px", color: "#111827", fontWeight: "600" };
const roleStyle = { margin: "4px 0", fontSize: "14px", color: "#6b7280" };
const membershipIdStyle = { fontSize: "13px", color: "#6b7280" };

const statusBadgeStyle = (status) => ({
  display: "inline-block",
  background:
    status === "approved" || status === "active" ? "#dcfce7" : "#fef9c3",
  color: status === "approved" || status === "active" ? "#16a34a" : "#ca8a04",
  borderRadius: "9999px",
  padding: "2px 10px",
  fontSize: "12px",
  fontWeight: "600",
  textTransform: "capitalize",
  marginBottom: "4px",
});

const mainLayout = {
  display: "flex",
  gap: "24px",
  alignItems: "flex-start",
  flexWrap: "wrap",
};

const quickInfoBox = {
  width: "250px",
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const quickInfoTitle = { fontSize: "15px", fontWeight: "600", color: "#111827", marginBottom: "4px" };

const infoItem = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  color: "#374151",
  fontSize: "14px",
};

const tabHeader = {
  display: "flex",
  borderBottom: "1px solid #e5e7eb",
  marginBottom: "16px",
  gap: "24px",
  fontWeight: "600",
  fontSize: "14px",
};

const tabButton = {
  cursor: "pointer",
  paddingBottom: "8px",
};

const tabContent = {
  background: "#f9fafb",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  padding: "20px",
  fontSize: "14px",
  color: "#111827",
  minHeight: "300px",
};

const sectionTitle = { fontSize: "16px", fontWeight: "600", marginBottom: "12px" };

const summaryGrid = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const summaryItem = {
  padding: "8px 0",
  borderBottom: "1px solid #f1f5f9",
};

const noDocumentsStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "40px 20px",
  color: "#6b7280",
  gap: "8px",
};

const errorBoxStyle = {
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: "6px",
  padding: "20px",
  color: "#dc2626",
  textAlign: "center",
};

const retryButtonStyle = {
  padding: "8px 16px",
  background: "#dc2626",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  marginTop: "10px",
};

export default UserDetailView;